import React, { useRef, useState } from 'react';
import { Grid, Button } from '@mui/material';
import InsightAccordionItem from '../InsightAccordionItem';
import EditorForm from '../EditorForm/EditorForm';
import { Add } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import DOMPurify from 'dompurify';

interface Insights {
  insightId: number;
  title: string;
  description: string;
  opsType: string;
  createdBy: string | null;
  updatedBy: string | null;
  createdDate: string;
  updatedDate: string;
  technology?: string | null;
  month?: string;
}

interface Insight {
  id?: number;
  title: string;
  description: string;
}

interface InsightsSectionProps {
  insights: Insights[];
  onAdd: (insight: Insight) => void;
  onUpdate: (insight: Insight) => void;
  onDelete: (id: number) => void;
}

const containsXssContent = (input: string): boolean => {
  if (!input) return false;
  const patterns = [
    /<\s*script\b/i,
    /&lt;\s*script\b/i,
    /<\s*\/\s*script\s*>/i,
    /&lt;\s*\/\s*script\s*&gt;/i,
    /<\s*iframe\b/i,
    /&lt;\s*iframe\b/i,
    /<\s*object\b/i,
    /&lt;\s*object\b/i,
    /<\s*embed\b/i,
    /&lt;\s*embed\b/i,
    /\bon\w+\s*=/i,
    /&lt;[^>]*\bon\w+\s*=/i,
    /\bjavascript\s*:/i,
    /\bvbscript\s*:/i,
  ];
  return patterns.some((pattern) => pattern.test(input));
};

const InsightsSection: React.FC<InsightsSectionProps> = ({
  insights,
  onAdd,
  onUpdate,
  onDelete,
}) => {
  const theme = useTheme();
  const [expandedInsightId, setExpandedInsightId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [currentInsight, setCurrentInsight] = useState<Insights | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const formRef = useRef<HTMLDivElement>(null);

  const handleOpen = (insight?: Insights) => {
    setErrorMessage('');
    if (insight) {
      setCurrentInsight(insight);
      setTitle(insight.title);
      setDescription(insight.description);
    } else {
      setCurrentInsight(null);
      setTitle('');
      setDescription('');
    }
    setShowForm(true);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleTitleChange = (val: string) => {
    setTitle(val);
    if (errorMessage) setErrorMessage('');
  };

  const handleDescriptionChange = (val: string) => {
    setDescription(val);
    if (errorMessage) setErrorMessage('');
  };

  const handleSave = () => {
    const trimmedTitle = title.trim();
    const trimmedDesc = description.trim();

    if (!trimmedTitle) {
      setErrorMessage('Title is required.');
      return;
    }

    if (!trimmedDesc || trimmedDesc === '<p></p>') {
      setErrorMessage('Description is required.');
      return;
    }

    if (containsXssContent(title) || containsXssContent(description)) {
      setErrorMessage('Script tags and Cross-Site Scripting (XSS) content are not allowed in Title or Description.');
      return;
    }

    setErrorMessage('');
    const cleanTitle = DOMPurify.sanitize(trimmedTitle, { ALLOWED_TAGS: [] }).trim();
    const cleanDescription = DOMPurify.sanitize(description);
    if (currentInsight) {
      const newInsight: Insight = {
        id: currentInsight.insightId,
        title: cleanTitle,
        description: cleanDescription,
      };

      onUpdate(newInsight);
    } else {
      const newInsight: Insight = {
        title: cleanTitle,
        description: cleanDescription,
      };
      onAdd(newInsight);
    }

    setShowForm(false);
  };

  const handleDelete = (InsightId: number) => {
    onDelete(InsightId);
  };

  const handleCancel = () => {
    setShowForm(false);
    setCurrentInsight(null);
    setTitle('');
    setDescription('');
    setErrorMessage('');
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {insights.map((insight) => (
          <InsightAccordionItem
            key={insight.insightId}
            insight={insight}
            expanded={expandedInsightId === insight.insightId}
            showForm={showForm}
            onToggle={() =>
              setExpandedInsightId((prev) =>
                prev === insight.insightId ? null : insight.insightId,
              )
            }
            onDelete={handleDelete}
            onEdit={handleOpen}
          />
        ))}
      </Grid>

      {!showForm && (
        <Grid item xs={12}>
          <Button
            variant="pepwiseButtonText"
            startIcon={<Add sx={{ color: `${theme.palette.buttons.primary.color} !important` }} />}
            onClick={() => handleOpen()}
            sx={{ color: `${theme.palette.buttons.primary.color} !important`, mb: 4 }}
          >
            Add Insights/Actions
          </Button>
        </Grid>
      )}

      {showForm && (
        <Grid item ref={formRef} xs={12}>
          <EditorForm
            title={title}
            description={description}
            onTitleChange={handleTitleChange}
            onDescriptionChange={handleDescriptionChange}
            onSave={handleSave}
            onCancel={handleCancel}
            titlePlaceholder="Title"
            descriptionPlaceholder="Description"
            errorMessage={errorMessage}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default InsightsSection;